/**
 * LandingPage — NODISTAR* White-Label AI Platform
 * Minimalist & bold redesign
 */

import { lazy, Suspense } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import SEOHead from "@/components/SEOHead";
import { FAQ_STRUCTURED_DATA } from "@/components/landing/sections/FAQSection";

import { LandingNav } from "@/components/landing/LandingNav";
import { LandingHero } from "@/components/landing/sections/LandingHero";
import { PlatformSection } from "@/components/landing/sections/PlatformSection";

const EBitsShowcase = lazy(() => import("@/components/landing/sections/EBitsShowcase").then(m => ({ default: m.EBitsShowcase })));
const MarketplaceSection = lazy(() => import("@/components/landing/sections/MarketplaceSection").then(m => ({ default: m.MarketplaceSection })));
const FeaturesGrid = lazy(() => import("@/components/landing/sections/FeaturesGrid").then(m => ({ default: m.FeaturesGrid })));
const UseCasesSection = lazy(() => import("@/components/landing/sections/UseCasesSection").then(m => ({ default: m.UseCasesSection })));
const TestimonialsSection = lazy(() => import("@/components/landing/sections/TestimonialsSection").then(m => ({ default: m.TestimonialsSection })));
const PricingSection = lazy(() => import("@/components/landing/sections/PricingSection").then(m => ({ default: m.PricingSection })));
const FAQSection = lazy(() => import("@/components/landing/sections/FAQSection").then(m => ({ default: m.FAQSection })));
const FinalCTA = lazy(() => import("@/components/landing/sections/FinalCTA").then(m => ({ default: m.FinalCTA })));
const LandingFooter = lazy(() => import("@/components/landing/LandingFooter").then(m => ({ default: m.LandingFooter })));

function SectionFallback() {
  return <div className="min-h-[100px]" />;
}

const STRUCTURED_DATA = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  "name": "NODISTAR*",
  "applicationCategory": "BusinessApplication",
  "operatingSystem": "Web",
  "description": "White-label AI platform — launch your own AI business under your brand. Currently in beta with 4 Suites and 11 Flows.",
  "url": "https://nodistar.com",
  "offers": {
    "@type": "AggregateOffer",
    "priceCurrency": "EUR",
    "lowPrice": "99",
    "highPrice": "499",
    "offerCount": "3",
  },
  "provider": {
    "@type": "Organization",
    "name": "NODISTAR*",
    "url": "https://nodistar.com",
  },
};

export default function LandingPage() {
  const { user, loading } = useAuth();

  if (user && !loading) {
    return <Navigate to="/app" replace />;
  }

  return (
    <>
      <SEOHead
        title="NODISTAR* — White-Label AI Platform (Beta)"
        description="White-label AI-platform met 4 Suites en 11 Flows. Jouw branding, jouw klanten, jouw recurring revenue. Nu in beta — vroege toegang beschikbaar."
        keywords="white-label AI platform, AI SaaS, AI business, NODISTAR, recurring revenue, beta"
        canonicalUrl="/"
        ogType="website"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(STRUCTURED_DATA) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(FAQ_STRUCTURED_DATA) }}
      />

      <div className="min-h-screen bg-background">
        <LandingNav />
        <main>
          <LandingHero />
          <PlatformSection />
          <Suspense fallback={<SectionFallback />}>
            <EBitsShowcase />
            <MarketplaceSection />
            <FeaturesGrid />
            <UseCasesSection />
            <TestimonialsSection />
            <PricingSection />
            <FAQSection />
            <FinalCTA />
          </Suspense>
        </main>
        <Suspense fallback={null}>
          <LandingFooter />
        </Suspense>
      </div>
    </>
  );
}
